import { Box, Divider, IconButton, Stack, Typography } from '@mui/material';
import { GridContainer, GridItem } from '../shared/ui-extension';
import PropertyCard from './property-card';
import ItemPropertyTable from './item-property-table';
import type { Item, ItemDetail } from './item-types';
import { CloudDownload } from '@mui/icons-material';
import ItemComponents from './item-components';
import {
  getTemplateUrl,
  makeItemInformationProperties,
  makeLabelProperties,
  makePrintProperties,
} from './property-helpers';
import { useEffect, useState, Fragment } from 'react';
import ItemImageGallery from './item-image-gallery';
import { ItemAPI } from '../../api/item';
import SubstituteItem from './substitute-item';
import { MatchingItemCard } from './matching-item-card';
import { LabeledInput } from '../../components/shared/labeled-input';
import ShippingSpec from './shipping-spec';
import { shippingSpecTypes } from '../../constants/shipping-spec-type';
import ItemStatuses from './item-statuses';

export default function ItemDetails({ item }: { item: Item }) {
  const gap = 1;

  const itemInformationProperties = makeItemInformationProperties(item);
  const labelProperties = makeLabelProperties(item);
  const printProperties = makePrintProperties(item);
  const showPrintAndLabel =
    printProperties.length > 0 || labelProperties.length > 0;

  const [templateDownloadUrl, setTemplateDownloadUrl] = useState<
    string | undefined
  >(undefined);
  const [printDownloadUrl, setPrintDownloadUrl] = useState<string | undefined>(
    undefined
  );
  const [matchingItemInput, setMatchingItemInput] = useState('');

  const [substitutes, setSubstitutes] = useState<ItemDetail[]>([]);
  const [matchingItems, setMatchingItems] = useState<ItemDetail[]>([]);

  useEffect(() => {
    const getTemplateUrls = async () => {
      for (const tt of ['label', 'print']) {
        const url = await getTemplateUrl(item, tt);
        switch (tt) {
          case 'label':
            setTemplateDownloadUrl(url);
          case 'print':
            setPrintDownloadUrl(url);
        }
      }
    };
    const getSubstitutes = async () => {
      if (item.substitutes.length == 0) {
        setSubstitutes([]);
        return;
      }
      const apiResult = await ItemAPI.getProductDetails(item.substitutes);
      let apiData = apiResult.data as ItemDetail[];
      apiData = apiData.sort((a, b) => {
        // Handle undefined cases - push to end
        if (a.qty_avail === undefined && b.qty_avail === undefined) return 0;
        if (a.qty_avail === undefined) return 1;
        if (b.qty_avail === undefined) return -1;

        // Sort descending (higher quantity first)
        return b.qty_avail - a.qty_avail;
      });
      setSubstitutes(apiData);
    };
    const getMatchingItems = async () => {
      if (item.matches.length == 0) {
        setMatchingItems([]);
        return;
      }
      const apiResult = await ItemAPI.getProductDetails(item.matches);
      let apiData = apiResult.data as ItemDetail[];
      apiData = apiData.sort((a, b) => {
        // Handle undefined cases - push to end
        if (a.qty_avail === undefined && b.qty_avail === undefined) return 0;
        if (a.qty_avail === undefined) return 1;
        if (b.qty_avail === undefined) return -1;

        // Sort descending (higher quantity first)
        return b.qty_avail - a.qty_avail;
      });
      setMatchingItems(apiData);
    };
    const getData = async () => {
      await Promise.all([
        getSubstitutes(),
        getTemplateUrls(),
        getMatchingItems(),
      ]);
    };
    getData();
  }, [item.item_id]);

  return (
    <GridContainer alignItems={'top'} xs={12} columns={12}>
      <GridItem xs={12} md={4} lg={2.7}>
        <Stack style={{ marginTop: '20px' }}>
          <ItemImageGallery
            itemId={item.item_id}
            imageCount={item.image_count}
          />
        </Stack>
      </GridItem>
      <GridItem xs={12} md={4} lg={2}>
        <Stack direction={'column'} gap={gap}>
          <PropertyCard title='Item Information'>
            <ItemPropertyTable properties={itemInformationProperties} />
          </PropertyCard>
          {item.components?.length > 0 && (
            <PropertyCard title='Components'>
              <ItemComponents components={item.components} />
            </PropertyCard>
          )}
          {item.disable_label_template ? (
            <PropertyCard title='Print & Label Information'>
              <Typography sx={{ padding: '2px' }}>
                Label information is not required for this item.
              </Typography>
            </PropertyCard>
          ) : showPrintAndLabel ? (
            <PropertyCard title='Print & Label Information'>
              {labelProperties.length > 0 && (
                <ItemPropertyTable properties={labelProperties} />
              )}
              {item.label_type_field && templateDownloadUrl && (
                <DownloadButton
                  name='Label Template'
                  url={templateDownloadUrl}
                />
              )}
              {printProperties.length > 0 && (
                <ItemPropertyTable properties={printProperties} />
              )}
              {item.label_type_field && printDownloadUrl && (
                <DownloadButton name='Print Template' url={printDownloadUrl} />
              )}
            </PropertyCard>
          ) : (
            <PropertyCard title='Print & Label Information'>
              <Typography sx={{ padding: '2px' }}>
                No information is available for this item.
              </Typography>
            </PropertyCard>
          )}
        </Stack>
      </GridItem>
      <GridItem xs={12} md={4} lg={2.3}>
        <Stack direction={'column'} gap={gap}>
          <ShippingSpec
            title={'Inbound'}
            specType={shippingSpecTypes.inbound}
            item={item}
          />
          <ShippingSpec
            title={'Outbound - Small Parcel'}
            specType={shippingSpecTypes.outboundSmallParcel}
            item={item}
          />
          <ShippingSpec
            title={'Outbound - LTL Shipments'}
            specType={shippingSpecTypes.outbound}
            item={item}
          />
        </Stack>
      </GridItem>
      <GridItem xs={12} md={4} lg={2.5}>
        <Stack direction={'column'} gap={gap}>
          <PropertyCard title='Substitutes'>
            <div style={{ padding: '4px' }}>
              {substitutes.length > 0 &&
                substitutes.map((sub, i) => (
                  <Fragment key={sub.item_id}>
                    <SubstituteItem key={sub.item_id} substitute={sub} />
                    {i < substitutes.length - 1 ? (
                      <Divider sx={{ my: 1 }} />
                    ) : null}
                  </Fragment>
                ))}
              {!substitutes.length && <Typography>No substitutes.</Typography>}
            </div>
          </PropertyCard>
          {item.parents?.length > 0 && (
            <PropertyCard title='Parents'>
              <Stack
                sx={{ margin: '5px', maxHeight: '525px', overflowY: 'auto' }}
              >
                <ItemComponents components={item.parents} />
              </Stack>
            </PropertyCard>
          )}
        </Stack>
      </GridItem>
      <GridItem xs={12} md={4} lg={2.5}>
        <PropertyCard title='Matching Items'>
          <Stack sx={{ margin: '5px', height: '600px', paddingTop: '3px' }}>
            <LabeledInput
              label='Filter Matching Item'
              value={matchingItemInput}
              updateOnChangeLive
              onChange={(e) => {
                const event = e as React.ChangeEvent<HTMLInputElement>;
                setMatchingItemInput(event.target.value);
              }}
              variant='outlined'
              placeholder={'(Type in description, supplier, product group...)'}
            />

            <Stack sx={{ overflowY: 'auto' }}>
              <Box />
              {matchingItems
                .filter(
                  ({
                    item_id,
                    extended_desc,
                    product_group,
                    supplier_name,
                  }) => {
                    if (!matchingItemInput) return true;

                    const inputTokens = matchingItemInput.toLowerCase();

                    const searchTokens = [
                      item_id,
                      extended_desc,
                      product_group,
                      supplier_name,
                    ]
                      .join(' ')
                      .toLowerCase();

                    return searchTokens.includes(inputTokens);
                  }
                )
                .sort((a, b) => (b?.qty_avail ?? 0) - (a?.qty_avail ?? 0))
                .map((item) => (
                  <MatchingItemCard key={item.inv_mast_uid} item={item} />
                ))}
            </Stack>
          </Stack>
        </PropertyCard>
      </GridItem>
      <GridItem xs={12} md={6} lg={3}>
        <ItemStatuses item={item} />
      </GridItem>
    </GridContainer>
  );
}

function DownloadButton({ name, url }: { name: string; url: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
      <IconButton color='primary' href={url} target='_blank'>
        <Stack direction={'row'} useFlexGap gap={1}>
          <CloudDownload />
          <Typography>{name}</Typography>
        </Stack>
      </IconButton>
    </div>
  );
}
